import { ModalForm, ProFormText, ProFormSelect } from '@ant-design/pro-form';
import { Button, Modal, message, Form } from 'antd';
import { Rule } from 'antd/lib/form';
import { get } from 'lodash-es';
import React, { FC } from 'react';
import { BusiCode } from '@/infrastructure/axios/code';
import {
  addTag,
  deleteTag,
  updateTag,
  TagInfo,
} from '@/presentation/pages/setting/tag-manage/api';
import { getColorLabel } from '@/presentation/pages/setting/tag-manage/columns';
import { TAG_COLOR_INFO } from '@/presentation/pages/setting/tag-manage/config';
import styles from './index.module.scss';

const { confirm } = Modal;

const getColorOptions = () => TAG_COLOR_INFO.map(item => ({
  label: getColorLabel(item.color, true),
  value: item.color,
}));

export const AddTagModal: FC<{
  tagList: TagInfo[],
  onSubmit: () => void,
}> = (props) => {
  const { onSubmit, tagList } = props;
  const [form] = Form.useForm();

  const onFinish = async (data: { tagName: string, tagColor: string }) => {
    const res = await addTag(data.tagName, data.tagColor);
    const code = get(res, 'code');
    if (code === BusiCode.Ok) {
      message.success('添加标签成功');
      onSubmit();
      return true;
    }
    message.error('添加标签失败');

    return false;
  };

  return <ModalForm
    title={'添加标签'}
    trigger={<Button type={'primary'}>新建标签</Button>}
    layout={'horizontal'}
    width={500}
    onFinish={onFinish}
    form={form}
    onVisibleChange={(visible) => {
      if (visible) {
        form.resetFields();
      }
    }}
  >
    <ProFormText
      label={'标签名称'}
      name={'tagName'}
      required={true}
      rules={getRules(tagList)}
    />
    <ProFormSelect
      label={'标签颜色'}
      name={'tagColor'}
      required={true}
      initialValue={getColorOptions()[0].value}
      options={getColorOptions()}
      allowClear={false}
    />
  </ModalForm>;
};

export const DeleteTagModal: FC<{
  tagId: string,
  onSubmit: () => void,
}> = (props) => {
  const { tagId, onSubmit } = props;

  const onDelete = async () => {
    const res = await deleteTag(tagId);
    const code = get(res, 'code');
    if (code === BusiCode.Ok) {
      message.success('删除标签成功');
      onSubmit();
      return true;
    }
    message.error('删除标签失败');

    return false;
  };

  const onClick = () => {
    confirm({
      title: `删除标签ID（${tagId}）`,
      content: '标签删除后，相应异常将不能通过该标签检索，删除后不可恢复',
      onOk: onDelete,
    });
  };

  return <Button type={'link'} className={styles.tableButton} onClick={onClick}>删除</Button>;
};

export const EditTagModal: FC<{
  tagId: string,
  tagName: string,
  tagColor: string,
  tagList: TagInfo[],
  onSubmit: () => void,
}> = (props) => {
  const { tagId, tagName, onSubmit, tagList, tagColor } = props;
  const [form] = Form.useForm();

  const onFinish = async (data: { tagName: string, tagColor: string }) => {
    const res = await updateTag(tagId, data.tagName, data.tagColor);
    const code = get(res, 'code');
    if (code === BusiCode.Ok) {
      message.success('编辑标签成功');
      onSubmit();
      return true;
    }
    message.error('编辑标签失败');

    return false;
  };

  return <ModalForm
    title={`编辑标签（标签ID：${tagId}）`}
    trigger={<Button className={styles.tableButton} type={'link'}>编辑</Button>}
    width={500}
    layout={'horizontal'}
    onFinish={onFinish}
    form={form}
    onVisibleChange={(visible) => {
      if (visible) {
        form.resetFields();
      }
    }}
  >
    <ProFormText
      label={'标签名称'}
      name={'tagName'}
      required={true}
      rules={getRules(tagList, tagName)}
      initialValue={tagName}
    />
    <ProFormSelect
      label={'标签颜色'}
      name={'tagColor'}
      required={true}
      initialValue={tagColor}
      options={getColorOptions()}
      allowClear={false}
    />
  </ModalForm>;
};

const getRules = (tagList: TagInfo[], tagName?: string): Rule[] => {
  const hasSameName = (rule: any, value: string, callback: any) => {
    if (value === tagName) {
      callback();
      return;
    }
    const findSameName = tagList.find(item => item.tagName === value);
    if (findSameName) {
      callback(new Error(rule.message));
      return;
    }
    callback();
  };

  return [
    { required: true, message: '标签名称不能为空' },
    { type: 'string', max: 30, message: '最多30个字符' },
    { validator: hasSameName, message: '标签名称已存在' },
  ];
};
